import type { RowComponentProps } from 'react-window';
import { Checkbox, HighlightedText, VirtualisedList } from '~/components/granular';
import type { DropdownObjectItem } from '../..';
import { DropdownItem, useDropdownContext } from '../..';

export function CheckboxItem({
  className,
  item,
  style,
}: {
  className?: string;
  item: string | DropdownObjectItem;
  style?: React.CSSProperties;
}) {
  const { searchValue, isItemSelected, getValue, getLabel } = useDropdownContext();

  return (
    <DropdownItem className={className} isSelected={isItemSelected(item)} item={item} style={style}>
      <Checkbox
        checked={isItemSelected(item)}
        label={<HighlightedText searchValue={searchValue} text={getLabel(item)} />}
        name={getValue(item)}
      />
    </DropdownItem>
  );
}

export function DropdownCheckboxItems({ className }: { className?: string }) {
  const { items, getLabel, getValue } = useDropdownContext();

  return items.map((item: DropdownObjectItem | string) => {
    const itemKey = `${getLabel(item).toLocaleLowerCase().replace(/\s/g, '-')}-${getValue(item)}`;
    return <CheckboxItem className={className} item={item} key={itemKey} />;
  });
}

type CheckboxRowProps = { items: (string | DropdownObjectItem)[]; className?: string };

function VirtualisedCheckboxItemRow({ style, index, items, className }: RowComponentProps<CheckboxRowProps>) {
  return <CheckboxItem className={className} item={items[index]} style={style} />;
}

export function VirtualisedDropdownCheckboxItems({
  className,
  itemSize = 36,
}: {
  className?: string;
  itemSize?: number;
  height?: number;
}) {
  const { items } = useDropdownContext();

  const itemCount = items.length ?? 0;
  const calculatedHeight = Math.min(itemCount * itemSize, itemCount ? 300 : 0);

  return (
    <VirtualisedList
      rowCount={itemCount}
      rowHeight={itemSize}
      style={{ height: calculatedHeight }}
      rowProps={{ items, className }}
      rowComponent={VirtualisedCheckboxItemRow}
    />
  );
}
